import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { FaHtml5, FaMarkdown } from "react-icons/fa";
import { FiFileText, FiMaximize2, FiMinimize2 } from "react-icons/fi";
import { useDidUpdate } from "rooks5";
import { NoteFormat } from "@store/canvas";
import { NotePreview } from "./NotePreview";
import { SavedAgo } from "./SavedAgo";
import Styles from "./styles.module.scss";

type NoteEditorProps = {
	note: string;
	onDiscard?: () => void;
	onSave?: (note: string) => void;
	isVisible: boolean;
	placeholder?: string;
	onChange?: (note: string) => void;
	actionEnabled?: boolean;
	hideActionButtons?: boolean;
	lastSaved?: Date | null;
	onFocus?: () => void;
	onBlur?: () => void;
	lastSavedPosition?: "BottomLeft" | "TopRight";
	/** Enables the GitHub-style Write/Preview toolbar and format selector. Used by workflow notes only. */
	formatsEnabled?: boolean;
	noteFormat?: NoteFormat;
	/** When this changes (e.g. workflow switch) the view mode is re-evaluated from the note content. */
	resetKey?: string | number;
	onFormatChange?: (format: NoteFormat) => void;
	/** Notifies the parent when the card enters/exits fullscreen so surrounding UI can hide conflicting controls. */
	onFullscreenChange?: (isFullscreen: boolean) => void;
	/** Hides the fullscreen toggle — used when the editor already sits inside an enlarged modal. */
	hideFullscreen?: boolean;
};

const NOTE_FORMATS: {
	value: NoteFormat;
	label: string;
	icon: JSX.Element;
}[] = [
	{ value: "text", label: "Text", icon: <FiFileText size={11} /> },
	{ value: "markdown", label: "Markdown", icon: <FaMarkdown size={12} /> },
	{ value: "html", label: "HTML", icon: <FaHtml5 size={11} /> },
];

export const NoteEditor = ({
	note,
	onDiscard,
	onSave,
	isVisible,
	placeholder,
	onChange,
	actionEnabled = true,
	hideActionButtons,
	lastSaved,
	onFocus,
	onBlur,
	lastSavedPosition = "BottomLeft",
	formatsEnabled = false,
	noteFormat = "text",
	resetKey,
	onFormatChange,
	onFullscreenChange,
	hideFullscreen = false,
}: NoteEditorProps) => {
	const ref = useRef<HTMLTextAreaElement>(null);
	const [viewMode, setViewMode] = useState<"write" | "preview">(
		formatsEnabled && note?.trim() ? "preview" : "write"
	);

	useDidUpdate(() => {
		setViewMode(formatsEnabled && note?.trim() ? "preview" : "write");
	}, [resetKey]);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [overrideTheme, setOverrideTheme] = useState(true);

	useEffect(() => {
		if (isVisible && ref.current && viewMode === "write") {
			// Move cursor to the end of the text for better editing experience.
			ref.current?.setSelectionRange(note?.length, note?.length);
		}
	}, [isVisible]);

	useEffect(() => {
		onFullscreenChange?.(isFullscreen);
	}, [isFullscreen]);

	useEffect(() => {
		if (!isVisible && isFullscreen) {
			setIsFullscreen(false);
		}
	}, [isVisible]);

	useEffect(() => {
		if (!isFullscreen) {
			return;
		}
		const exitOnEsc = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setIsFullscreen(false);
			}
		};
		document.addEventListener("keydown", exitOnEsc);
		return () => document.removeEventListener("keydown", exitOnEsc);
	}, [isFullscreen]);

	const editorBody =
		formatsEnabled && viewMode === "preview" ? (
			<NotePreview
				note={note}
				format={noteFormat}
				overrideTheme={overrideTheme}
			/>
		) : (
			<textarea
				ref={ref}
				autoFocus
				value={note}
				className={Styles.textArea}
				onFocus={onFocus}
				onBlur={onBlur}
				onChange={(e) => {
					if (note === e.target.value) return;
					onChange?.(e.target.value);
				}}
				name="note"
				placeholder={placeholder ?? "Add here..."}
			/>
		);

	return (
		<div
			className={classNames(Styles.noteEditorWrapper, {
				[Styles.withToolbar]: formatsEnabled,
			})}
		>
			{lastSavedPosition === "TopRight" && (
				<div className={classNames(Styles.lastSaved, Styles.topRight)}>
					{lastSaved && <SavedAgo time={lastSaved} />}
				</div>
			)}
			{formatsEnabled ? (
				<div
					className={classNames(Styles.noteCard, {
						[Styles.fullscreen]: isFullscreen,
					})}
				>
					<div className={Styles.noteToolbar}>
						<div className={Styles.toolbarLeft}>
							<div className={Styles.viewTabs}>
								<button
									type="button"
									className={classNames(Styles.viewTab, {
										[Styles.active]: viewMode === "write",
									})}
									onClick={() => setViewMode("write")}
								>
									Raw
								</button>
								<button
									type="button"
									className={classNames(Styles.viewTab, {
										[Styles.active]: viewMode === "preview",
									})}
									onClick={() => setViewMode("preview")}
								>
									Preview
								</button>
							</div>
							{viewMode === "preview" && noteFormat !== "text" && (
								<label
									className={Styles.themeToggle}
									title="Recolour the content's light background and plain text to match the app theme. Uncheck to see the original content."
								>
									<input
										type="checkbox"
										checked={overrideTheme}
										onChange={(e) =>
											setOverrideTheme(e.target.checked)
										}
									/>
									Override Theme
								</label>
							)}
						</div>
						<div className={Styles.formatSelector}>
							{NOTE_FORMATS.map((f) => (
								<button
									type="button"
									key={f.value}
									title={f.label}
									className={classNames(Styles.formatBtn, {
										[Styles.active]: noteFormat === f.value,
									})}
									onClick={() => onFormatChange?.(f.value)}
								>
									{f.icon}
									<span>{f.label}</span>
								</button>
							))}
							{!hideFullscreen && (
								<button
									type="button"
									title={
										isFullscreen
											? "Exit fullscreen (Esc)"
											: "Fullscreen"
									}
									className={Styles.fullscreenBtn}
									onClick={() => setIsFullscreen((v) => !v)}
								>
									{isFullscreen ? (
										<FiMinimize2 size={12} />
									) : (
										<FiMaximize2 size={12} />
									)}
								</button>
							)}
						</div>
					</div>
					{editorBody}
				</div>
			) : (
				editorBody
			)}
			{lastSavedPosition === "BottomLeft" && (
				<div className={Styles.lastSaved}>
					{lastSaved && <SavedAgo time={lastSaved} />}
				</div>
			)}
			{hideActionButtons || (
				<div className={Styles.noteEditorActions}>
					<button
						disabled={!actionEnabled}
						className={"btn-sm " + Styles.cancelBtn}
						onClick={() => {
							onDiscard?.();
						}}
					>
						Discard Changes
					</button>
					<button
						disabled={!actionEnabled}
						className="btn-sm btn-yellow"
						onClick={() => {
							onSave?.(note);
						}}
					>
						Save
					</button>
				</div>
			)}
		</div>
	);
};
